import React, { useRef, useState, useEffect } from "react";
import * as tf from "@tensorflow/tfjs";
import CheckupQuiz from "./CheckupQuiz";
import FakeAnalyzeModal from "./FakeAnalyzeModal";
import FakeGallery from "./FakeGallery";

const MODEL_URL = "https://teachablemachine.withgoogle.com/models/ugFL3oxKT/model.json";
const METADATA_URL = "https://teachablemachine.withgoogle.com/models/ugFL3oxKT/metadata.json";
const LEVEL3_PASSWORD = "OBJEKTE";

type Prediction = {
    label: string;
    confidence: number;
};

type Props = {
    onRestart: () => void;
    onNextLevel: () => void;
    avatarImage: string;
    groupName: string;
    onBack: () => void;
    unlocked?: boolean; // <--- NEU
    unlockLevel?: () => void; // <--- NEU
    initialLevelStep?: number; // <--- NEU
};

export default function Level3({ onRestart, onNextLevel, avatarImage, groupName, onBack, unlocked, unlockLevel, initialLevelStep }: Props) {
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [model, setModel] = useState<tf.LayersModel | null>(null);
    const [labels, setLabels] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [videoReady, setVideoReady] = useState(false);

    const [showTestModal, setShowTestModal] = useState(false);
    const [showResultModal, setShowResultModal] = useState(false);
    const [currentPredictions, setCurrentPredictions] = useState<Prediction[]>([]);
    const [capturedImageUrl, setCapturedImageUrl] = useState<string | null>(null);
    const [showTrainingModal, setShowTrainingModal] = useState(false);
    const [showTrainingGallery, setShowTrainingGallery] = useState(false);
    const [showFakeAnalyzeModal, setShowFakeAnalyzeModal] = useState(false);
    const [showAnalyzeModal, setShowAnalyzeModal] = useState(false);
    const [showFakeGallery, setShowFakeGallery] = useState(false);

    const [levelStep, setLevelStep] = useState(initialLevelStep ?? 0);

    // Passwort-Modal State
    const [showPasswordModal, setShowPasswordModal] = useState(!unlocked);
    const [passwordInput, setPasswordInput] = useState("");
    const [passwordError, setPasswordError] = useState("");

    // Definition der Quizfragen für CheckupQuiz
    const quizQuestion1 = {
        question: "Welche KI erkennt einen Gegenstand besser?",
        options: [
            { label: "A) Eine KI, die mit 400 Bildern trainiert ist.", value: "A" },
            { label: "B) Eine KI, die mit 10.000 Bildern trainiert ist.", value: "B" },
        ],
        correctAnswer: "B",
        explanation: "Die KI, die mit 10.000 Bildern trainiert wurde, erkennt den Gegenstand besser, weil sie lernen konnte, wie der Gegenstand aus verschiedenen Blickwinkeln aussieht.",
    };

    const quizQuestion2 = {
        question: "Wenn eine KI nur ein Bild von einer grünen Banane gesehen hat, was passiert, wenn sie eine gelbe Banane sieht?",
        options: [
            { label: "A) Sie ist sich nicht ganz sicher!", value: "A" },
            { label: "B) Sie erkennt sie ganz sicher!", value: "B" },
        ],
        correctAnswer: "A",
        explanation: "Wenn sie nur ein Beispiel kennt, weiß sie nicht wie unterschiedlich etwas aussehen soll.",
    };


    useEffect(() => {
        async function loadModelAndMetadata() {
            setLoading(true);
            try {
                const loadedModel = await tf.loadLayersModel(MODEL_URL);
                setModel(loadedModel);
                const metadataResponse = await fetch(METADATA_URL);
                const metadata = await metadataResponse.json();
                setLabels(metadata.labels);
            } catch (err) {
                console.error("Fehler beim Laden des Modells oder der Metadaten in Level 3:", err);
            } finally {
                setLoading(false);
            }
        }
        loadModelAndMetadata();
    }, []);

    useEffect(() => {
        if (!showTestModal) {
            setVideoReady(false);
            if (videoRef.current?.srcObject) {
                (videoRef.current.srcObject as MediaStream)
                    .getTracks()
                    .forEach((track) => track.stop());
            }
            return;
        }

        async function setupCamera() {
            if (!videoRef.current) return;
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ video: true });
                videoRef.current.srcObject = stream;
                videoRef.current.onloadedmetadata = () => {
                    if (videoRef.current) {
                        videoRef.current.play();
                        setVideoReady(true);
                    }
                };
            } catch (err) {
                console.error("Fehler beim Zugriff auf die Kamera in Level 3:", err);
                setVideoReady(false);
            }
        }

        setupCamera();

        return () => {
            if (videoRef.current?.srcObject) {
                (videoRef.current.srcObject as MediaStream)
                    .getTracks()
                    .forEach((track) => track.stop());
            }
        };
    }, [showTestModal]);

    const handleTestphotoClick = () => {
        setShowTestModal(true);
        setCapturedImageUrl(null); // Bild beim Öffnen des Modals zurücksetzen
        setCurrentPredictions([]);
        setLevelStep(2); // Geht zum Testmodal
    };

    // --- FIX: showAnalyzeModal statt FakeAnalyzeModal nach Testfoto ---
    const captureAndClassifyTestImage = async () => {
        if (
            !model ||
            !videoRef.current ||
            !canvasRef.current ||
            !videoReady ||
            videoRef.current.videoWidth === 0 ||
            videoRef.current.videoHeight === 0
        ) {
            console.warn("Kann kein Bild aufnehmen: Modell nicht geladen, Kamera nicht bereit oder Video-Dimensionen 0.");
            return;
        }

        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;
        ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);

        setCapturedImageUrl(canvas.toDataURL('image/png'));

        // Zeige das "Analysiere Bild..." Modal (wie in Level 5)
        setShowTestModal(false);
        setLevelStep(3); // <-- Zeige den Ladebalken

        // Simuliere eine kurze Analysezeit
        await new Promise(resolve => setTimeout(resolve, 1500));

        let imgTensor = tf.browser.fromPixels(canvas);
        imgTensor = tf.image.resizeBilinear(imgTensor, [224, 224]).div(255).expandDims(0);

        const predictionTensor = model.predict(imgTensor) as tf.Tensor;
        const predictionData = await predictionTensor.data();
        tf.dispose([imgTensor, predictionTensor]);

        const predictions = labels
            .map((label, i) => ({
                label,
                confidence: predictionData[i],
            }))
            .sort((a, b) => b.confidence - a.confidence);

        setCurrentPredictions(predictions);
        setShowResultModal(true);
        setLevelStep(4); // Nach dem Ladebalken direkt zum Ergebnis
    };

    const handleResultModalClose = () => {
        setShowResultModal(false);
        setLevelStep(4);
    };

    const handleQuizComplete = (passed: boolean) => {
        console.log("Quiz beendet! Bestanden:", passed);
        setLevelStep(6); // Geht zum Abschluss nach dem Quiz
    };

    // Passwort-Modal
    if (showPasswordModal) {
        return (
            <div style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                background: "rgba(0,0,0,0.7)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 2000
            }}>
                <div style={{
                    backgroundColor: "#fff",
                    borderRadius: "15px",
                    padding: "40px",
                    boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
                    minWidth: "320px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "20px"
                }}>
                    <h2 style={{ marginBottom: 10, color: "#333" }}>Passwort eingeben für Level 3</h2>
                    <p style={{ color: "#555", fontSize: "1.1em", textAlign: "center" }}>
                        Gebt hier das Passwort ein, um zu Level 3 zu gelangen:
                    </p>
                    <input
                        type="password"
                        value={passwordInput}
                        onChange={e => { setPasswordInput(e.target.value); setPasswordError(""); }}
                        style={{
                            padding: "10px",
                            fontSize: "1.2em",
                            borderRadius: "8px",
                            border: "1px solid #ccc",
                            width: "200px",
                            marginBottom: "10px"
                        }}
                        autoFocus
                        onKeyDown={e => {
                            if (e.key === "Enter") {
                                if (passwordInput.trim().toUpperCase() === LEVEL3_PASSWORD) {
                                    setShowPasswordModal(false);
                                    unlockLevel && unlockLevel();
                                } else {
                                    setPasswordError("Falsches Passwort");
                                }
                            }
                        }}
                    />
                    <div style={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                        gap: "15px"
                    }}>
                        <button
                            onClick={onBack}
                            style={{
                                backgroundColor: "#f0f0f0",
                                color: "#555",
                                border: "1px solid #ccc",
                                borderRadius: "8px",
                                padding: "10px 25px",
                                fontSize: "1.1em",
                                cursor: "pointer"
                            }}
                        >
                            Abbrechen
                        </button>
                        <button
                            onClick={() => {
                                if (passwordInput.trim().toUpperCase() === LEVEL3_PASSWORD) {
                                    setShowPasswordModal(false);
                                    unlockLevel && unlockLevel();
                                } else {
                                    setPasswordError("Falsches Passwort");
                                }
                            }}
                            style={{
                                backgroundColor: "#4c84ff",
                                color: "white",
                                border: "none",
                                borderRadius: "8px",
                                padding: "10px 25px",
                                fontSize: "1.1em",
                                cursor: "pointer"
                            }}
                        >
                            Bestätigen
                        </button>
                    </div>
                    {passwordError && (
                        <div style={{ color: "red", fontWeight: 600 }}>{passwordError}</div>
                    )}
                </div>
            </div>
        );
    }

    // Show FakeAnalyzeModal first, then FakeGallery
    if (showFakeAnalyzeModal) {
        return (
            <FakeAnalyzeModal
                images={[
                    "https://upload.wikimedia.org/wikipedia/commons/thumb/9/99/Apples_in_basket_2018_G2.jpg/640px-Apples_in_basket_2018_G2.jpg",
                    "https://upload.wikimedia.org/wikipedia/commons/thumb/4/40/Bananas_on_countertop.JPG/640px-Bananas_on_countertop.JPG",
                    "https://upload.wikimedia.org/wikipedia/commons/thumb/2/23/Se%C3%B1orita_banana_%28Mindanao%2C_Philippines%29_10.jpg/640px-Se%C3%B1orita_banana_%28Mindanao%2C_Philippines%29_10.jpg",
                    "https://upload.wikimedia.org/wikipedia/commons/thumb/d/d3/Banana_caturra.JPG/640px-Banana_caturra.JPG",
                    "https://upload.wikimedia.org/wikipedia/commons/thumb/9/9b/USDA_Eat_Healthy_Food_Group_Gallery_serving_size_small_apple.jpg/640px-USDA_Eat_Healthy_Food_Group_Gallery_serving_size_small_apple.jpg",
                    "https://upload.wikimedia.org/wikipedia/commons/thumb/7/78/Banana_mutant.jpg/640px-Banana_mutant.jpg",
                    "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a5/9897Ants_of_the_Philippines_eating_latundan_banana_28.jpg/640px-9897Ants_of_the_Philippines_eating_latundan_banana_28.jpg",
                    "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c7/Appleontable.jpg/640px-Appleontable.jpg",
                    "https://upload.wikimedia.org/wikipedia/commons/thumb/3/30/Pomological_Watercolor_POM00000781.jpg/640px-Pomological_Watercolor_POM00000781.jpg",
                    "https://upload.wikimedia.org/wikipedia/commons/thumb/a/ab/Apple-2535260_1920.jpg/640px-Apple-2535260_1920.jpg",
                    "https://upload.wikimedia.org/wikipedia/commons/thumb/0/0b/Hi_definition_apple_cartoon_with_transparent_background.png/640px-Hi_definition_apple_cartoon_with_transparent_background.png",
                    "https://upload.wikimedia.org/wikipedia/commons/thumb/5/5b/Nopple_apple.jpg/640px-Nopple_apple.jpg",
                    
                ]}
                onFinish={() => {
                    setShowFakeAnalyzeModal(false);
                    setShowFakeGallery(true);
                }}
            />
        );
    }

    if (showFakeGallery) {
        return (
            <FakeGallery
                images={Array.from({ length: 200 }, (_, index) => `/gallery/${index + 1}.jpg`)}
                onFinish={() => {
                    setShowFakeGallery(false);
                    setLevelStep(1); // Continue with the next step after gallery
                }}
            />
        );
    }

    // Fügt die fehlende Funktion hinzu
    const handleWeitereBilderLaden = () => {
        setShowFakeAnalyzeModal(true);
        setLevelStep(1);
    };

    return (
        <div style={{
            fontFamily: 'Nunito, sans-serif',
            backgroundColor: '#f7f9fc',
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'flex-start',
            padding: '30px',
            gap: '30px',
        }}>
            <div style={{
                flexGrow: 1,
                backgroundColor: '#fff',
                borderRadius: '15px',
                padding: '30px',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.05)',
                position: 'relative',
            }}>
                {/* Restart Button top right INSIDE the white card, styled like Level 1 */}
                <button
                    onClick={onRestart}
                    style={{
                        position: 'absolute',
                        top: 15,
                        right: 15,
                        backgroundColor: '#f0f0f0',
                        color: '#555',
                        border: '1px solid #ccc',
                        borderRadius: '8px',
                        padding: '8px 18px',
                        cursor: 'pointer',
                        fontSize: '1.1em',
                        fontWeight: 600,
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                        transition: 'background-color 0.2s ease',
                        zIndex: 10,
                    }}
                    onMouseOver={e => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                    onMouseOut={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                >
                    🔄 Level neu starten
                </button>

                {/* Header-Bereich mit Avatar und Level-Titel */}
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '30px' }}>
                    <div style={{
                        width: '120px',
                        height: '120px',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        border: '4px solid #4c84ff',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                        marginRight: '20px',
                        flexShrink: 0,
                    }}>
                        <img src={avatarImage} alt="Team Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>

                    <div style={{ flexGrow: 1 }}>
                        <h2 style={{ fontSize: '2.5em', fontWeight: '800', color: '#333', marginBottom: '10px' }}>
                            Level 3: Je mehr Daten, desto besser!
                        </h2>
                        <p style={{ fontSize: '1.4em', color: '#555' }}>
                            Testet die KI mit neuen Bildern!
                        </p>
                    </div>
                </div>

                {/* Phasenabhängiger Inhalt */}
                {levelStep === 0 && ( // Einführungstext
                    <div style={{ ...contentBoxStyle }}>
                        <p style={{ ...paragraphStyle }}>
                            Ich habe schon einiges verstanden, aber um besser zu werden, brauche ich noch mehr Bilder.
                            Zeigt mir unbedingt noch mehr Bilder! Wenn ihr auf 'Weitere Bilder laden' klickt, kann ich mit den weiteren Fotos dazulernen.
                        </p>
                        <div style={buttonRowStyle}>
                            <div /> {/* No Zurück button on first step */}
                            <button onClick={handleWeitereBilderLaden} style={{ ...buttonStyle }}>
                                Weitere Bilder laden
                            </button>
                        </div>
                    </div>
                )}

                {/* Phasenabhängiger Inhalt */}
                {levelStep === 1 && (
                    <div style={{ ...contentBoxStyle }}>
                        <p style={{ ...paragraphStyle }}>
                            Super gemacht, {groupName}! Ihr habt mich mit vielen neuen Trainingsdaten trainiert. Drückt "Weiter", damit ich euch zeigen kann, was ich jetzt alles gelernt habe. Ich bin ganz aufgeregt! 🤩
                        </p>
                        <div style={buttonRowStyle}>
                            <button
                                onClick={() => setLevelStep(0)}
                                style={{ ...buttonStyle, backgroundColor: '#f0f0f0', color: '#555', border: '1px solid #ccc' }}
                                onMouseOver={e => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                                onMouseOut={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                            >
                                Zurück
                            </button>
                            <button onClick={() => setLevelStep(2)} style={{ ...buttonStyle }}>
                                Weiter
                            </button>
                        </div>
                    </div>
                )}

                {levelStep === 2 && (
                    <div style={{ ...contentBoxStyle }}>
                        <p style={{ ...paragraphStyle }}>
                            Testet mich! 📷 Schießt nochmal ein neues Foto von einem Apfel oder einer Banane und schaut, wie gut ich den Unterschied erkenne. Erzählt danach eurer Klasse, wie gut ich die Objekte erkannt habe!
                        </p>
                        <div style={buttonRowStyle}>
                            <button
                                onClick={() => setLevelStep(1)}
                                style={{ ...buttonStyle, backgroundColor: '#f0f0f0', color: '#555', border: '1px solid #ccc' }}
                                onMouseOver={e => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                                onMouseOut={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                            >
                                Zurück
                            </button>
                            <button onClick={handleTestphotoClick} style={{ ...buttonStyle }}>
                                Weiter
                            </button>
                        </div>
                    </div>
                )}

                {levelStep === 4 && (
                    <div style={{ ...contentBoxStyle }}>
                        <h3 style={{ ...headingStyle, marginBottom: '10px' }}>
                            Wusstest du schon?
                        </h3>
                        <p style={{ ...paragraphStyle }}>
                            Trainingsdaten sind alle Bilder, Texte und Töne, die der KI beim Lernen helfen. Wenn die KI nur wenige und gleiche Bilder sieht, denkt sie, Äpfel sehen immer gleich aus. Erst durch viele verschiedene Beispiele lernt sie: Äpfel können ganz unterschiedlich sein! 🍎🍏
                        </p>
                        <div style={buttonRowStyle}>
                            <button
                                onClick={() => {
                                    setShowResultModal(true);
                                    setLevelStep(3);
                                }}
                                style={{ ...buttonStyle, backgroundColor: '#f0f0f0', color: '#555', border: '1px solid #ccc' }}
                                onMouseOver={e => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                                onMouseOut={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                            >
                                Zurück
                            </button>
                            <button onClick={() => setLevelStep(5)} style={{ ...buttonStyle }}>
                                Weiter
                            </button>
                        </div>
                    </div>
                )}

                {levelStep === 5 && (
                    <div style={buttonRowStyle}>
                        {/* <button
                            onClick={() => setLevelStep(4)}
                            style={{ ...buttonStyle, backgroundColor: '#f0f0f0', color: '#555', border: '1px solid #ccc' }}
                            onMouseOver={e => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                            onMouseOut={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                        >
                            Zurück
                        </button> */}
                        <div style={{ flex: 1 }}>
                            <CheckupQuiz
                                question1={quizQuestion1}
                                question2={quizQuestion2}
                                onQuizComplete={handleQuizComplete}
                                onBack={() => setLevelStep(4)} // Go back to previous step
                            />
                        </div>
                    </div>
                )}

                {levelStep === 6 && (
                    <div style={{ ...contentBoxStyle }}>
                        <p style={{ ...paragraphStyle }}>
                            Super gemacht, Gruppe "{groupName}"! 😊
                            Klickt "Weiter", um zu Level 4 zu gelangen.
                        </p>
                        <div style={buttonRowStyle}>
                            <button
                                onClick={() => setLevelStep(5)}
                                style={{ ...buttonStyle, backgroundColor: '#f0f0f0', color: '#555', border: '1px solid #ccc' }}
                                onMouseOver={e => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                                onMouseOut={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                            >
                                Zurück
                            </button>
                            <button onClick={onNextLevel} style={{ ...buttonStyle, backgroundColor: '#4caf50' }}
                                onMouseOver={e => e.currentTarget.style.backgroundColor = '#45a049'}
                                onMouseOut={e => e.currentTarget.style.backgroundColor = '#4caf50'}>
                                Weiter
                            </button>
                        </div>
                    </div>
                )}



                {/* Test-Modal (nur für einen Test verwendet) */}
                {showTestModal && (
                    <div style={{ ...modalOverlayStyle }}>
                        <div style={{ ...modalContentStyle }}>
                            <h3 style={{ ...headingStyle, marginBottom: '15px', textAlign: 'center' }}>
                                Level 3: Nehmt ein Testbild auf!
                            </h3>
                            <p style={{ ...paragraphStyle, textAlign: 'center' }}>
                                Nehmt ein neues Testbild auf!
                            </p>
                            <video
                                ref={videoRef}
                                style={{ ...videoStyle, display: videoReady ? 'block' : 'none' }}
                                autoPlay
                                muted
                                playsInline
                            />
                            {!videoReady && (
                                <div style={{ ...videoPlaceholderStyle }}>
                                    Kamera wird geladen...
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: '20px', marginTop: '20px' }}>
                                <button onClick={() => { setShowTestModal(false); setLevelStep(2); }} style={{ ...buttonStyle, backgroundColor: '#d9534f' }}
                                        onMouseOver={e => e.currentTarget.style.backgroundColor = '#c9302c'} onMouseOut={e => e.currentTarget.style.backgroundColor = '#d9534f'}>
                                    Abbrechen
                                </button>
                                <button onClick={captureAndClassifyTestImage} disabled={!videoReady || loading} style={{
                                    ...buttonStyle,
                                    backgroundColor: '#4c84ff',
                                    cursor: (!videoReady || loading) ? 'not-allowed' : 'pointer',
                                    opacity: (!videoReady || loading) ? 0.6 : 1,
                                }} onMouseOver={e => !(!videoReady || loading) && (e.currentTarget.style.backgroundColor = '#3b6dd6')} onMouseOut={e => !(!videoReady || loading) && (e.currentTarget.style.backgroundColor = '#4c84ff')}>
                                    Weiter
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Analysiere Bild Modal (Frame 13) */}
                {levelStep === 3 && (
                    <div style={{ ...modalOverlayStyle }}>
                        <div style={{ ...modalContentStyle }}>
                            <h3 style={{ ...headingStyle, textAlign: 'center' }}>
                                Level 3: Je mehr Daten, desto besser!
                            </h3>
                            <p style={{ ...paragraphStyle, textAlign: 'center' }}>
                                Analysiere Bild...
                            </p>
                            {/* Einfacher Ladebalken */}
                            <div style={{ width: '80%', height: '10px', backgroundColor: '#f0f0f0', borderRadius: '5px' }}>
                                <div style={{
                                    width: '100%',
                                    height: '100%',
                                    backgroundColor: '#4c84ff',
                                    borderRadius: '5px',
                                    animation: 'loading-bar 1.5s forwards linear',
                                }} />
                            </div>
                            <style>
                                {`
                                @keyframes loading-bar {
                                    0% { width: 0%; }
                                    100% { width: 100%; }
                                }
                                `}
                            </style>
                        </div>
                    </div>
                )}

                {/* Ergebnis-Modal */}
                {showResultModal && (
                    <div style={{ ...modalOverlayStyle }}>
                        <div style={{ ...modalContentStyle, maxWidth: '500px' }}>
                            <h3 style={{ ...headingStyle, textAlign: 'center' }}>
                                Ergebnisse:
                            </h3>
                            {capturedImageUrl && (
                                <img
                                    src={capturedImageUrl}
                                    alt="Aufgenommenes Bild"
                                    style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain', marginBottom: '15px', border: '2px solid #ddd', borderRadius: '8px' }}
                                />
                            )}

                            {currentPredictions.map((prediction, index) => (
                                <div key={index} style={{ width: '100%', marginBottom: '10px' }}>
                                    <p style={{ fontSize: '1.1em', color: '#555', marginBottom: '5px' }}>
                                        {prediction.label}: {Math.round(prediction.confidence * 100)}%
                                    </p>
                                    <div style={{
                                        width: '100%',
                                        backgroundColor: '#f0f0f0',
                                        borderRadius: '5px',
                                        height: '15px',
                                        overflow: 'hidden',
                                    }}>
                                        <div style={{
                                            width: `${prediction.confidence * 100}%`,
                                            height: '100%',
                                            backgroundColor: prediction.label === "Apfel" ? '#FF6347' : '#FFD700',
                                            borderRadius: '5px',
                                        }} />
                                    </div>
                                </div>
                            ))}

                            {/* Warnhinweis */}
                            <p style={{
                                color: 'red',
                                fontWeight: 'bold',
                                fontSize: '1.1em',
                                marginTop: '10px',
                                textAlign: 'center',
                            }}>
                                Macht einen Screenshot vom Ergebnis!
                            </p>

                            {/* Footer with two buttons */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                gap: '15px',
                marginTop: '10px'
            }}>
                <button
                    onClick={() => {
                        setShowResultModal(false);
                        setCapturedImageUrl(null);
                        setCurrentPredictions([]);
                        setShowTestModal(true);
                        setVideoReady(false);
                    }}
                    style={{
                        backgroundColor: '#f0f0f0',
                        color: '#555',
                        border: '1px solid #ccc',
                        borderRadius: '8px',
                        padding: '12px 25px',
                        cursor: 'pointer',
                        fontSize: '1.1em',
                        fontWeight: 600,
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                        transition: 'background-color 0.2s ease',
                    }}
                    onMouseOver={e => e.currentTarget.style.backgroundColor = '#e0e0e0'}
                    onMouseOut={e => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                >
                    Nochmal ausprobieren
                </button>
                <button onClick={handleResultModalClose} style={{
                    backgroundColor: '#4c84ff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px 25px',
                    cursor: 'pointer',
                    fontSize: '1.1em',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    transition: 'background-color 0.2s ease',
                }} onMouseOver={e => e.currentTarget.style.backgroundColor = '#3b6dd6'} onMouseOut={e => e.currentTarget.style.backgroundColor = '#4c84ff'}>
                    Weiter
                </button>
            </div>
        </div>
    </div>
)}

                {showTrainingModal && (
                    <FakeAnalyzeModal
                        images={[
                            "https://upload.wikimedia.org/wikipedia/commons/a/a0/SenegalBaobab.jpg",
                            "https://upload.wikimedia.org/wikipedia/commons/thumb/5/56/Sequoiadendron_giganteum_at_Kenilworth_Castle.jpg/250px-Sequoiadendron_giganteum_at_Kenilworth_Castle.jpg",
                            "https://upload.wikimedia.org/wikipedia/commons/a/a0/SenegalBaobab.jpg",
                            "https://upload.wikimedia.org/wikipedia/commons/thumb/5/56/Sequoiadendron_giganteum_at_Kenilworth_Castle.jpg/250px-Sequoiadendron_giganteum_at_Kenilworth_Castle.jpg",
                        ]}
                        onFinish={() => {
                            setShowTrainingModal(false);
                            setShowResultModal(true);
                            setLevelStep(3);
                        }}
                    />
                )}

                {showTrainingGallery && (
                    <FakeGallery
                        images={Array.from(
                            { length: 200 },
                            (value, index) => `/gallery/${index + 1}.jpg`
                        )}
                        onFinish={() => alert("weiter")}
                    />
                )}


                <canvas ref={canvasRef} style={{ display: "none" }} />
            </div>
        </div>
    );
}

// Gemeinsame Stile (unverändert)
const contentBoxStyle = {
    display: 'flex',
    flexDirection: 'column' as 'column',
    alignItems: 'flex-start',
    gap: '20px',
    backgroundColor: '#e6f7ff',
    border: '2px solid #91d1ff',
    borderRadius: '10px',
    padding: '25px',
    maxWidth: '700px',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
    marginTop: '30px',
};

const paragraphStyle = {
    fontSize: '1.3em',
    color: '#555',
    lineHeight: '1.6',
};

const headingStyle = {
    fontSize: '1.8em',
    color: '#333',
};

const buttonStyle = {
    marginTop: '20px',
    backgroundColor: '#4c84ff',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 25px',
    cursor: 'pointer',
    fontSize: '1.1em',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    transition: 'background-color 0.2s ease',
};

const modalOverlayStyle = {
    position: "fixed" as 'fixed',
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    background: "rgba(0,0,0,0.6)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
};

const modalContentStyle = {
    backgroundColor: '#fff',
    borderRadius: '15px',
    padding: '40px',
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
    maxWidth: '600px',
    width: '90%',
    display: 'flex',
    flexDirection: 'column' as 'column',
    alignItems: 'center',
    gap: '25px',
    position: 'relative' as 'relative',
};

const videoStyle = {
    border: "3px solid #ddd",
    borderRadius: '10px',
    maxWidth: '100%',
    maxHeight: '400px',
};

const videoPlaceholderStyle = {
    width: '100%',
    height: '300px',
    backgroundColor: '#eee',
    borderRadius: '10px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#888',
};

const buttonRowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginTop: '30px',
    gap: '10px',
};